import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EditorContent, useEditor } from '@tiptap/react';
import { EditorState, Selection, TextSelection } from '@tiptap/pm/state';

import { useWikiV2Client, wikiErrorCode, wikiErrorStatus } from '../../../hooks/useWikiV2';
import { errorText } from '../../../lib/apiErrors';
import { countWords } from '../markdown/wikiMarkdown';
import WikiIcon from '../shared/WikiIcon';
import { WikiLoading, WikiNoAccess } from '../shared/WikiStates';
import useWikiRole from '../shared/useWikiRole';
import { wikiKeys } from '../shared/useWikiQueries';
import { wikiUi } from '../shared/wikiUi';
import WikiEditorToolbar from './WikiEditorToolbar';
import WikiLinkBar from './WikiLinkBar';
import WikiSlashMenu from './WikiSlashMenu';
import {
    conflictMessage, EDITOR_COPY, editorMeta, editorStats, editorStatus, publishLabel, saveToast,
} from './editorCopy';
import { currentToolbarKey } from './tiptap/commands';
import { applyLinkToView, buildWikiEditorExtensions, runWikiCommand } from './tiptap/extensions';
import { docToMarkdown, docWordCount, markdownToDoc } from './tiptap/markdownIO';
import { closeSlash, pickSlash, WIKI_LOAD_META, wikiSlashKey } from './tiptap/slash';
import useLeaveGuard from './useLeaveGuard';
import './editor.css';

const enc = encodeURIComponent;
/** Presigned asset URLs live 5 minutes (BE-core); share the page view's map. */
const ASSET_MAP_STALE_MS = 4 * 60 * 1000;

/** The Markdown the editor opens: the writer's draft when there is one, else the published body. */
export function editorSource(page) {
    if (!page) return '';
    if (page.status === 'draft' || page.has_draft) return String(page.draft_md ?? page.body_md ?? '');
    return String(page.body_md ?? '');
}

/** The version the edit starts from (`base_version` for draft/publish). */
export function editorBaseVersion(page) {
    if (!page) return 0;
    if (page.has_draft && page.draft_base_version != null) return Number(page.draft_base_version) || 0;
    return Number(page.version) || 0;
}

/** Prototype caret on load: a new page → start of the first empty paragraph (else the last block); else the first block. */
function loadSelection(doc, newPage) {
    if (newPage) {
        let target = null;
        doc.forEach((node, offset) => {
            if (target === null && node.type.name === 'paragraph' && !node.textContent.trim()) target = offset + 1;
        });
        if (target !== null) return TextSelection.create(doc, target);
        let last = 0;
        doc.forEach((node, offset) => { last = offset; });
        return Selection.near(doc.resolve(last + 1));
    }
    return Selection.atStart(doc);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * The keyboard exit (WCAG 2.1.2 — Tab stays inside the surface, as in the
 * prototype): the first focusable control after the surface in document
 * order, else `fallback` (the Publish button).
 */
export function focusAfterSurface(surface, fallback) {
    if (!surface) return null;
    const doc = surface.ownerDocument;
    const next = [...doc.querySelectorAll(FOCUSABLE)].find((el) => (
        !surface.contains(el)
        && !el.closest('[hidden]')
        && (surface.compareDocumentPosition(el) & 4) // DOCUMENT_POSITION_FOLLOWING
    ));
    const target = next || fallback || null;
    if (typeof surface.blur === 'function') surface.blur();
    if (target && typeof target.focus === 'function') target.focus();
    return target;
}

function saveErrorText(err) {
    const code = wikiErrorCode(err);
    if (code === 'wiki_page_archived') return 'This page was archived, so it can’t be saved.';
    if (code === 'wiki_publish_conflict') return 'Someone else is publishing this page right now. Try again in a moment.';
    return errorText(err, 'The page couldn’t be saved. Your changes are still here — try again.');
}

/**
 * `<WikiPageEditor page onExit [isNew] [onSaved] />` — the inline page editor
 * (prototype `wkEditing`, C1-editor.md). Rendered by page/WikiPage on
 * `/wiki/p/:id/edit` inside the page's main column (crumbs above it, the
 * cheat sheet + rail beside it).
 *
 * - `page`: the full Page from `GET /wiki/pages/{id}` (writers get
 *   `draft_md` / `draft_base_version`).
 * - `onExit()`: called after Cancel (clean), Discard, Save draft and Publish;
 *   WikiPage invalidates and navigates back to the read view.
 * - `isNew` (optional): caret in the first empty paragraph on load. Defaults
 *   to the New-page modal's hand-off, `location.state.wikiEditor.focusOnLoad`
 *   (or `.isNew`).
 * - `onSaved(savedPage, {publish})` (optional): called before `onExit` with
 *   the API response.
 */
export default function WikiPageEditor({ page, onExit, isNew, onSaved }) {
    const role = useWikiRole();
    const location = useLocation();
    const handoff = (location && location.state && location.state.wikiEditor) || null;
    const newPage = typeof isNew === 'boolean'
        ? isNew
        : Boolean(handoff && (handoff.focusOnLoad || handoff.isNew));

    if (!page || role.loading) {
        return (
            <div className="wk-card" data-testid="wiki-editor-loading">
                <WikiLoading label={EDITOR_COPY.loading} />
            </div>
        );
    }
    if (!role.canEdit || page.can_edit === false) {
        return (
            <div className="wk-card" data-testid="wiki-editor-no-access">
                <WikiNoAccess title={EDITOR_COPY.noAccessTitle} detail={EDITOR_COPY.noAccessDetail} />
                {onExit && (
                    <div className="wk-ed-noaccess-actions">
                        <button type="button" className="wk-btn wk-btn--secondary wk-btn--sm" onClick={() => onExit()}>
                            {EDITOR_COPY.backToPage}
                        </button>
                    </div>
                )}
            </div>
        );
    }
    return (
        <WikiEditorSession
            key={page.page_id}
            page={page}
            onExit={onExit}
            onSaved={onSaved}
            newPage={newPage}
        />
    );
}

function WikiEditorSession({ page, onExit, onSaved, newPage }) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    // Frozen at open: what was loaded and the version it was based on.
    const [opened] = useState(() => ({
        source: editorSource(page),
        baseVersion: editorBaseVersion(page),
        wasDraft: page.status === 'draft',
        version: Number(page.version) || 0,
        status: page.status,
    }));
    const [title, setTitle] = useState(() => page.title || '');
    const [mode, setMode] = useState('write');
    const [source, setSource] = useState(opened.source);
    const [dirty, setDirty] = useState(false);
    const [domDirty, setDomDirty] = useState(false);
    const [words, setWords] = useState(null);
    const [loadKey, setLoadKey] = useState(1);
    const [confirm, setConfirm] = useState(null);
    const [link, setLink] = useState(null);
    const [busy, setBusy] = useState(null);
    const [conflict, setConflict] = useState(null);
    const [error, setError] = useState(null);
    const [, rerender] = useReducer((n) => n + 1, 0);

    const sourceRef = useRef(opened.source);
    const modeRef = useRef('write');
    const domDirtyRef = useRef(false);
    const loadDocRef = useRef(null);
    const linkCtxRef = useRef(null);
    const allowLeaveRef = useRef(false);
    const wordTimerRef = useRef(null);
    const newPageRef = useRef(newPage);
    const wrapRef = useRef(null);
    const textareaRef = useRef(null);
    const runCommandRef = useRef(() => false);
    const escapeRef = useRef(() => {});
    const publishRef = useRef(null);
    const resolveAssetRef = useRef(() => Promise.resolve(null));
    const busyRef = useRef(false);

    const extensions = useMemo(() => buildWikiEditorExtensions({
        resolveAsset: (id) => resolveAssetRef.current(id),
        runCommand: (kind) => runCommandRef.current(kind),
        onEscape: () => escapeRef.current(),
    }), []);
    const editor = useEditor({
        extensions,
        editorProps: {
            attributes: {
                class: 'wk-ed-surface',
                'data-wysi': '1',
                'data-testid': 'wiki-editor-surface',
                'aria-label': 'Page content',
                'aria-multiline': 'true',
                'aria-description': 'Press Escape to leave the editor',
                role: 'textbox',
            },
        },
        immediatelyRender: true,
        shouldRerenderOnTransaction: false,
    }, []);

    const resolveAsset = useCallback(async (assetId) => {
        const map = await queryClient.fetchQuery({
            queryKey: [...wikiKeys.page(page.page_id), 'assets'],
            queryFn: () => client.get(`/wiki/pages/${enc(page.page_id)}/assets`),
            staleTime: ASSET_MAP_STALE_MS,
        }).catch(() => null);
        const hit = map && map.assets && map.assets[assetId];
        if (hit && hit.url) return hit.url;
        const one = await client.get(`/wiki/assets/${enc(assetId)}?format=json`).catch(() => null);
        return (one && one.url) || null;
    }, [client, queryClient, page.page_id]);

    const openLink = useCallback((ctx) => {
        linkCtxRef.current = ctx;
        setLink({ kind: ctx.kind, url: ctx.url || '', n: Date.now() });
        if (editor && !editor.isDestroyed) closeSlash(editor.view);
    }, [editor]);

    // Keep the refs the editor's extensions call into current (declared before
    // the load effect so asset images resolve on the first load).
    useEffect(() => {
        resolveAssetRef.current = resolveAsset;
        runCommandRef.current = (kind) => (editor && !editor.isDestroyed
            ? runWikiCommand(editor.view, kind, { openLink })
            : false);
        escapeRef.current = () => {
            if (editor && !editor.isDestroyed) focusAfterSurface(editor.view.dom, publishRef.current);
        };
    });

    const markDomChanged = useCallback(() => {
        setDirty(true);
        if (!domDirtyRef.current) {
            domDirtyRef.current = true;
            setDomDirty(true);
        }
        clearTimeout(wordTimerRef.current);
        wordTimerRef.current = setTimeout(() => {
            if (editor && !editor.isDestroyed) setWords(docWordCount(editor.state.doc));
        }, 400);
    }, [editor]);

    // Transactions: dirty tracking, link-bar range mapping, toolbar refresh.
    useEffect(() => {
        if (!editor) return undefined;
        const onTransaction = ({ transaction }) => {
            if (transaction.docChanged) {
                const ctx = linkCtxRef.current;
                if (ctx) {
                    linkCtxRef.current = {
                        ...ctx,
                        from: transaction.mapping.map(ctx.from, -1),
                        to: transaction.mapping.map(ctx.to, 1),
                    };
                }
                if (!transaction.getMeta(WIKI_LOAD_META)) markDomChanged();
            }
            rerender();
        };
        editor.on('transaction', onTransaction);
        return () => editor.off('transaction', onTransaction);
    }, [editor, markDomChanged]);

    useEffect(() => () => clearTimeout(wordTimerRef.current), []);

    // (Re)load the Write surface from the Markdown source: on open and on every
    // Markdown → Write switch (prototype `loadKey`). A fresh state also resets
    // undo history, so Ctrl/Cmd+Z never "undoes" the load.
    useEffect(() => {
        if (!editor || editor.isDestroyed || modeRef.current !== 'write') return undefined;
        const doc = markdownToDoc(sourceRef.current, editor.schema);
        const state = EditorState.create({
            doc,
            plugins: editor.state.plugins,
            selection: loadSelection(doc, newPageRef.current),
        });
        editor.view.updateState(state);
        loadDocRef.current = doc;
        newPageRef.current = false;
        rerender();
        const t = setTimeout(() => {
            if (!editor.isDestroyed) editor.view.focus();
        }, 30);
        return () => clearTimeout(t);
    }, [editor, loadKey]);

    /** The Markdown to store: the source verbatim unless the Write surface really changed. */
    const currentMd = useCallback(() => {
        if (modeRef.current === 'md' || !editor || editor.isDestroyed || !domDirtyRef.current) return sourceRef.current;
        const { doc } = editor.state;
        if (loadDocRef.current && doc.eq(loadDocRef.current)) return sourceRef.current;
        return docToMarkdown(doc, editor.schema);
    }, [editor]);

    const exit = useCallback(() => {
        allowLeaveRef.current = true;
        if (onExit) onExit();
    }, [onExit]);

    useLeaveGuard({
        when: dirty,
        allowRef: allowLeaveRef,
        onBlock: (proceed) => setConfirm({ proceed }),
    });

    const switchMode = (next) => {
        if (next === modeRef.current) return;
        if (next === 'md') {
            const md = currentMd();
            sourceRef.current = md;
            setSource(md);
            domDirtyRef.current = false;
            setDomDirty(false);
            setLink(null);
            linkCtxRef.current = null;
            if (editor && !editor.isDestroyed) closeSlash(editor.view);
            modeRef.current = 'md';
            setMode('md');
            return;
        }
        domDirtyRef.current = false;
        setDomDirty(false);
        setWords(null);
        modeRef.current = 'write';
        setMode('write');
        setLoadKey((k) => k + 1);
    };

    const onTitleKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== 'ArrowDown') return;
        event.preventDefault();
        if (modeRef.current === 'md') {
            if (textareaRef.current) textareaRef.current.focus();
            return;
        }
        if (!editor || editor.isDestroyed) return;
        const { state } = editor.view;
        editor.view.dispatch(state.tr.setSelection(Selection.atStart(state.doc)));
        editor.view.focus();
    };

    const applyLink = () => {
        const current = link;
        if (!current) return;
        const ctx = linkCtxRef.current;
        setLink(null);
        linkCtxRef.current = null;
        if (!String(current.url || '').trim()) {
            cancelLink(ctx);
            return;
        }
        if (editor && !editor.isDestroyed) applyLinkToView(editor.view, ctx, current.url);
    };

    const cancelLink = (ctxArg) => {
        const ctx = ctxArg !== undefined ? ctxArg : linkCtxRef.current;
        setLink(null);
        linkCtxRef.current = null;
        if (!editor || editor.isDestroyed) return;
        const { state } = editor.view;
        if (ctx && Number.isFinite(ctx.from)) {
            const max = state.doc.content.size;
            try {
                const from = Math.min(ctx.from, max);
                editor.view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, from, Math.max(from, Math.min(ctx.to, max)))));
            } catch { /* keep the current selection */ }
        }
        editor.view.focus();
    };

    const onCancel = () => {
        if (dirty) setConfirm({});
        else exit();
    };

    const onDiscard = () => {
        const pending = confirm && confirm.proceed;
        setConfirm(null);
        if (pending) pending();
        else exit();
    };

    const save = async (publish, overrideBase) => {
        if (busyRef.current) return;
        busyRef.current = true;
        const md = currentMd();
        const nextTitle = title.trim() || page.title || '';
        const base = overrideBase !== undefined ? overrideBase : opened.baseVersion;
        const body = { base_version: base, body_md: md };
        if (nextTitle) body.title = nextTitle;
        setBusy(publish ? 'publish' : 'draft');
        setError(null);
        setConflict(null);
        try {
            const path = `/wiki/pages/${enc(page.page_id)}/${publish ? 'publish' : 'draft'}`;
            const saved = publish ? await client.post(path, body) : await client.put(path, body);
            if (saved && saved.page_id) {
                const { reconfirm_count: _ignored, ...pageDoc } = saved;
                queryClient.setQueryData(wikiKeys.page(page.page_id), pageDoc);
            }
            wikiUi.toast(saveToast({
                publish,
                wasDraft: opened.wasDraft && overrideBase === undefined,
                saved: saved || {},
            }));
            busyRef.current = false;
            setBusy(null);
            if (onSaved) onSaved(saved, { publish });
            exit();
        } catch (err) {
            busyRef.current = false;
            setBusy(null);
            const detail = err && err.response && err.response.data && err.response.data.detail;
            const isConflict = wikiErrorStatus(err) === 409
                && (wikiErrorCode(err) === 'version_conflict' || (detail && detail.error === 'version_conflict'));
            if (isConflict) {
                setConflict({ current: Number(detail && detail.current_version) || 0, publish });
                return;
            }
            setError(saveErrorText(err));
        }
    };

    const runCommand = (kind) => runCommandRef.current(kind);
    const toolbarKey = editor && !editor.isDestroyed && mode === 'write' ? currentToolbarKey(editor.state) : 'p|';
    const slash = editor && !editor.isDestroyed && mode === 'write' ? wikiSlashKey.getState(editor.state) : null;
    let slashX = 0;
    let slashY = 0;
    if (slash && slash.open && wrapRef.current) {
        try {
            const coords = editor.view.coordsAtPos(editor.state.selection.from);
            const wrap = wrapRef.current.getBoundingClientRect();
            slashX = Math.max(0, Math.min(Math.round(coords.left - wrap.left), Math.round(wrap.width - 296)));
            slashY = Math.round(coords.bottom - wrap.top + 6);
        } catch { /* no layout (tests): top-left */ }
    }
    const shownWords = mode === 'write' && domDirty && words != null ? words : countWords(source);
    const statusPage = { status: opened.status, version: opened.version };
    const rows = Math.max(18, String(source).split('\n').length + 3);

    return (
        <section className="wk-card wk-card--roomy wk-ed" data-testid="wiki-editor" aria-label="Edit page">
            <div className="wk-ed-head" data-testid="wiki-editor-header">
                <div className="wk-ed-row1">
                    <span className="wk-ed-status" data-testid="wiki-editor-status">
                        <WikiIcon name="lucide:pencil" size={12} />
                        {editorStatus(statusPage)}
                    </span>
                    <span className="wk-ed-stats" data-testid="wiki-editor-stats">{editorStats(shownWords)}</span>
                    <span className="wk-ed-spacer" />
                    {confirm ? (
                        <>
                            <span className="wk-ed-confirm-text" role="alert">{EDITOR_COPY.confirmDiscard}</span>
                            <button type="button" className="wk-ed-btn wk-ed-btn--ghost" data-testid="wiki-editor-keep" onClick={() => setConfirm(null)}>
                                {EDITOR_COPY.keepEditing}
                            </button>
                            <button type="button" className="wk-ed-btn wk-ed-btn--danger" data-testid="wiki-editor-discard" onClick={onDiscard}>
                                {EDITOR_COPY.discard}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="wk-ed-btn wk-ed-btn--ghost" data-testid="wiki-editor-cancel" onClick={onCancel}>
                                {EDITOR_COPY.cancel}
                            </button>
                            {opened.wasDraft && (
                                <button
                                    type="button"
                                    className="wk-ed-btn wk-ed-btn--secondary"
                                    data-testid="wiki-editor-save-draft"
                                    aria-busy={busy === 'draft'}
                                    onClick={() => save(false)}
                                >
                                    {busy === 'draft' ? EDITOR_COPY.saving : EDITOR_COPY.saveDraft}
                                </button>
                            )}
                            <button
                                ref={publishRef}
                                type="button"
                                className="wk-ed-btn wk-ed-btn--primary"
                                data-testid="wiki-editor-publish"
                                aria-busy={busy === 'publish'}
                                onClick={() => save(true)}
                            >
                                <WikiIcon name="lucide:check" size={14} />
                                {busy === 'publish' ? EDITOR_COPY.publishing : publishLabel(statusPage)}
                            </button>
                        </>
                    )}
                </div>
                <WikiEditorToolbar mode={mode} toolbarKey={toolbarKey} onMode={switchMode} onCommand={runCommand} />
                <WikiLinkBar
                    link={link}
                    onChange={(url) => setLink((l) => (l ? { ...l, url } : l))}
                    onApply={applyLink}
                    onCancel={() => cancelLink()}
                />
                {conflict && (
                    <div className="wk-ed-alert" role="alert" data-testid="wiki-editor-conflict">
                        <WikiIcon name="lucide:alert-triangle" size={15} />
                        <span className="wk-ed-alert-text">{conflictMessage(conflict.current, conflict.publish)}</span>
                        <button
                            type="button"
                            className="wk-ed-btn wk-ed-btn--secondary"
                            data-testid="wiki-editor-overwrite"
                            onClick={() => save(conflict.publish, conflict.current)}
                        >
                            {EDITOR_COPY.overwrite}
                        </button>
                        <button type="button" className="wk-ed-btn wk-ed-btn--ghost" onClick={() => setConflict(null)}>
                            {EDITOR_COPY.keepEditing}
                        </button>
                    </div>
                )}
                {error && (
                    <div className="wk-ed-alert wk-ed-alert--error" role="alert" data-testid="wiki-editor-error">
                        <WikiIcon name="lucide:alert-triangle" size={15} />
                        <span className="wk-ed-alert-text">{error}</span>
                    </div>
                )}
            </div>

            <input
                className="wk-ed-title"
                data-testid="wiki-editor-title"
                aria-label="Page title"
                value={title}
                placeholder={EDITOR_COPY.titlePlaceholder}
                onChange={(event) => {
                    setTitle(event.target.value);
                    setDirty(true);
                }}
                onKeyDown={onTitleKeyDown}
            />
            <div className="wk-ed-meta" data-testid="wiki-editor-meta">{editorMeta(page)}</div>

            <div className="wk-ed-write" ref={wrapRef} hidden={mode !== 'write'} data-testid="wiki-editor-write">
                <EditorContent editor={editor} />
                {mode === 'write' && (
                    <WikiSlashMenu
                        slash={slash}
                        x={slashX}
                        y={slashY}
                        onPick={(kind) => {
                            if (editor && !editor.isDestroyed) pickSlash(editor.view, kind, (v, k) => runCommandRef.current(k));
                        }}
                    />
                )}
            </div>

            {mode === 'md' && (
                <div className="wk-ed-md" data-testid="wiki-editor-markdown">
                    <p className="wk-ed-md-intro">{EDITOR_COPY.markdownIntro}</p>
                    <textarea
                        ref={textareaRef}
                        data-wkmd="1"
                        data-testid="wiki-editor-textarea"
                        aria-label="Stored Markdown"
                        className="wk-ed-textarea"
                        value={source}
                        rows={rows}
                        spellCheck={false}
                        onChange={(event) => {
                            sourceRef.current = event.target.value;
                            setSource(event.target.value);
                            setDirty(true);
                        }}
                    />
                </div>
            )}
        </section>
    );
}
